import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
// Orchard: hierarchical selectability + clearing discipline (Memory > Condition >
// Colour). Pure logic lives in the @orchard module; this file is DOM glue. See
// VariantSelectionNewUX.tla for the verified model.
import { calculateEnabledOptions, hierarchyRank, lowerRankIndices, RANK_COLOUR } from '@orchard/variant-availability';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * @typedef {{ id: number, available: boolean, option1: string|null, option2: string|null, option3: string|null }} OrchardVariant
 */

/**
 * A custom element that manages a variant picker.
 *
 * Orchard: Dawn-parity progressive filtering. Availability is computed locally
 * from the inline `#orchard-variant-data` JSON on every change; the server is
 * only contacted when the current selection resolves to an actual variant. This
 * avoids Horizon's default behavior of having the server auto-pick a variant
 * from a partial `option_values=` request, which surfaces unavailable variants
 * as "selected" on sparse catalogs (used-phone inventory).
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {OrchardVariant[] | null} */
  #variantDataCache = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#syncFieldsetState();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    this.#wireClearSelection();
    // #updateAvailability iterates mark→deselect until stable, which covers
    // the "URL points at an unavailable variant" case by deselecting the
    // checked+aria-disabled radios the server rendered.
    this.#updateAvailability();
    this.#updateAddToCartState();
    this.#syncUrlToSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   *
   * Orchard: on the main PDP we gate the server fetch on a complete selection
   * that resolves to an *available* variant. On partial or invalid selections
   * we update local availability and the ATC label only — no server round-trip,
   * no URL mutation, no title/price re-render.
   *
   * Outside the main PDP (product cards, quick-add dialogs, featured-product
   * sections on non-matching templates) we fall back to Horizon's original
   * flow. Those pickers don't emit the local variant catalog and rely on the
   * server to pick a variant from `option_values=`.
   *
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Orchard: clearing discipline — selecting a higher-priority option clears
    // every strictly-lower one, so the shopper never sits on an incompatible
    // combo (VariantSelectionNewUX.tla). No-ops on symmetric/non-phone pickers.
    if (event.target instanceof HTMLInputElement) {
      this.#clearLowerRankSelections(event.target);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Local availability pass. Safe to call in all contexts: pickers without a
    // local variant catalog (product cards, quick-add) no-op inside.
    this.#updateAvailability();

    const matchedVariant = isOnProductPage && !loadsNewProduct ? this.#findVariantForCurrentSelection() : null;
    const matchedAvailable = matchedVariant !== null && matchedVariant.available;

    this.#updateAddToCartState();

    // Dawn-parity gate only applies on the main PDP. Partial/invalid selection
    // → no fetch, no event dispatch, strip stale ?variant=, exit early.
    if (isOnProductPage && !loadsNewProduct && !matchedAvailable) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('variant')) {
        url.searchParams.delete('variant');
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }
      return;
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      const variantId = matchedVariant ? String(matchedVariant.id) : selectedOption.dataset.variantId || null;
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Reads the variant catalog emitted inside this <variant-picker>. Scoped to
   * the instance so combined-listing morphs of <main> swap in the new
   * product's catalog atomically. Cache is invalidated whenever the picker
   * subtree changes (see #invalidateVariantDataCache).
   * @returns {OrchardVariant[]}
   */
  #getVariantData() {
    if (this.#variantDataCache) return this.#variantDataCache;
    const el = this.querySelector('script[data-orchard-variant-data]');
    if (!el || !el.textContent) {
      this.#variantDataCache = [];
      return this.#variantDataCache;
    }
    try {
      this.#variantDataCache = JSON.parse(el.textContent);
    } catch {
      this.#variantDataCache = [];
    }
    return this.#variantDataCache || [];
  }

  /**
   * Returns rendered fieldsets in DOM order. We intentionally read from the
   * current subtree instead of relying only on refs because morph() can replace
   * picker children after a variant fetch.
   * @returns {HTMLFieldSetElement[]}
   */
  #getFieldsets() {
    return Array.from(this.querySelectorAll('fieldset.variant-option'));
  }

  /**
   * Rebuilds radio/index bookkeeping from the current DOM. Must run after
   * morphing the picker so selected-pill measurements and stale-selection
   * cleanup operate on live inputs, not pre-morph nodes.
   */
  #syncFieldsetState() {
    this.#radios = [];
    this.#checkedIndices = [];

    this.#getFieldsets().forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      this.#radios.push(radios);

      // Push one entry per fieldset so #radios and #checkedIndices stay in
      // lockstep. Without this, fieldsets that start with no checked radio
      // (e.g. unavailable URL variants deselected on load) leave an undefined
      // slot, and updateSelectedOption skips the pill-width bookkeeping on
      // their first click.
      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
  }

  #invalidateVariantDataCache() {
    this.#variantDataCache = null;
  }

  /**
   * Returns [option1, option2, option3] for the current picker state, using
   * document order to map to option dimensions (Liquid renders options in
   * forloop order whether they render as fieldset or single-value div).
   * BNIB's collapsed single-value div contributes its fixed value.
   * @returns {Array<string|null>}
   */
  #getCurrentSelections() {
    const options = this.querySelectorAll('.variant-option');
    /** @type {Array<string|null>} */
    const selections = [null, null, null];
    options.forEach((el, i) => {
      if (i > 2) return;
      const single = el.querySelector('[data-testid="variant-option-single-value"]');
      if (single) {
        selections[i] = (single.textContent || '').trim() || null;
        return;
      }
      const checkedRadio = el.querySelector('input[type="radio"]:checked');
      if (checkedRadio instanceof HTMLInputElement) {
        selections[i] = checkedRadio.value;
        return;
      }
      const select = el.querySelector('select');
      if (select instanceof HTMLSelectElement && select.value) {
        selections[i] = select.value;
      }
    });
    return selections;
  }

  /**
   * @returns {OrchardVariant | null} The variant whose options exactly match
   *   the current selection, or null if selection is partial / no match.
   */
  #findVariantForCurrentSelection() {
    const selections = this.#getCurrentSelections();
    const variants = this.#getVariantData();
    if (!variants.length) return null;

    const [o1, o2, o3] = selections;
    // Count how many option dimensions this product actually has by looking at
    // the first variant.
    const first = variants[0];
    if (!first) return null;
    const dims = [first.option1, first.option2, first.option3].filter((v) => v !== null).length;

    if (dims >= 1 && o1 === null) return null;
    if (dims >= 2 && o2 === null) return null;
    if (dims >= 3 && o3 === null) return null;

    return (
      variants.find((v) => {
        if (dims >= 1 && v.option1 !== o1) return false;
        if (dims >= 2 && v.option2 !== o2) return false;
        if (dims >= 3 && v.option3 !== o3) return false;
        return true;
      }) || null
    );
  }

  /**
   * Writes aria-disabled / data-option-available on every radio input based on
   * which values appear in an available variant compatible with the current
   * partial selection. Mirrors Dawn's renderAvailability.
   *
   * After marking, deselects any checked input that just became disabled and
   * re-runs until the picker reaches a stable state (caps iterations to
   * guard against pathological data).
   */
  #updateAvailability() {
    const variants = this.#getVariantData();
    if (!variants.length) return;

    let clearedStaleSelection = false;

    // Iterate: (1) mark aria-disabled from current selections, (2) clear
    // selections that are now disabled, (3) repeat if anything changed so
    // the other dimensions' availability reflects the cleared selections.
    for (let i = 0; i < 4; i++) {
      this.#markAvailability(variants);
      if (!this.#clearStaleSelections()) break;
      clearedStaleSelection = true;
    }

    if (clearedStaleSelection) {
      this.updateVariantPickerCss();
    }
  }

  /**
   * Orchard: hierarchy rank per option dimension, read from
   * data-orchard-option-name on each .variant-option in document order (the same
   * indexing #getCurrentSelections uses). Unknown names → null, which makes the
   * availability module fall back to symmetric filtering for non-phone products.
   * @returns {Array<number|null>}
   */
  #getRanks() {
    const options = this.querySelectorAll('.variant-option');
    /** @type {Array<number|null>} */
    const ranks = [];
    options.forEach((el, i) => {
      if (i > 2) return;
      ranks[i] = hierarchyRank(/** @type {HTMLElement} */ (el).dataset.orchardOptionName ?? null);
    });
    return ranks;
  }

  /**
   * Writes aria-disabled / data-option-available on every radio, using the
   * hierarchical selectability from @orchard/variant-availability (Memory never
   * filtered; Condition filtered by Memory; Colour by Memory + Condition).
   * @param {OrchardVariant[]} variants
   */
  #markAvailability(variants) {
    const selections = this.#getCurrentSelections();
    const ranks = this.#getRanks();
    const fieldsets = this.querySelectorAll('fieldset.variant-option');

    fieldsets.forEach((fs) => {
      // data-option-index is the real product option dimension (option1/2/3).
      // data-fieldset-index is a rendered-fieldset counter used by the base
      // class for #radios/#checkedIndices bookkeeping — do NOT use it here.
      const indexAttr = /** @type {HTMLElement} */ (fs).dataset.optionIndex;
      const oi = indexAttr !== undefined ? parseInt(indexAttr, 10) : NaN;
      if (Number.isNaN(oi)) return;

      const availableValues = new Set(calculateEnabledOptions(variants, selections, oi, ranks));

      fs.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        if (availableValues.has(input.value)) {
          input.removeAttribute('aria-disabled');
          input.setAttribute('data-option-available', 'true');
        } else {
          input.setAttribute('aria-disabled', 'true');
          input.setAttribute('data-option-available', 'false');
        }
      });
    });
  }

  /**
   * Deselects any checked radio that is also aria-disabled. Covers both the
   * initial-load case (URL pointing at an unavailable variant) and the
   * mid-session case (a selection that was valid until another option
   * changed).
   *
   * @returns {boolean} true if at least one input was deselected.
   */
  #clearStaleSelections() {
    let changed = false;
    this.querySelectorAll('fieldset.variant-option input[type="radio"]:checked[aria-disabled="true"]').forEach(
      (input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.checked = false;
        input.removeAttribute('checked');
        input.dataset.currentChecked = 'false';
        changed = true;
      }
    );
    if (changed) {
      // Keep #checkedIndices in sync so base class pill-width bookkeeping
      // doesn't index into stale entries.
      this.#syncFieldsetState();
    }
    return changed;
  }

  /**
   * Orchard: when an option is selected, deselect every radio in a
   * strictly-lower-priority dimension (Memory clears Condition+Colour; Condition
   * clears Colour; Colour clears nothing). lowerRankIndices returns [] on
   * symmetric/non-phone pickers, so this no-ops there.
   * @param {HTMLInputElement} input - the just-selected radio
   * @returns {boolean} true if anything was deselected
   */
  #clearLowerRankSelections(input) {
    const oiAttr = input.dataset.optionIndex;
    const oi = oiAttr !== undefined ? parseInt(oiAttr, 10) : NaN;
    if (Number.isNaN(oi)) return false;

    const toClear = lowerRankIndices(this.#getRanks(), oi);
    if (!toClear.length) return false;

    let changed = false;
    this.querySelectorAll('fieldset.variant-option').forEach((fs) => {
      const fsOiAttr = /** @type {HTMLElement} */ (fs).dataset.optionIndex;
      const fsOi = fsOiAttr !== undefined ? parseInt(fsOiAttr, 10) : NaN;
      if (!toClear.includes(fsOi)) return;
      fs.querySelectorAll('input[type="radio"]').forEach((r) => {
        if (!(r instanceof HTMLInputElement) || !r.checked) return;
        r.checked = false;
        r.removeAttribute('checked');
        r.dataset.currentChecked = 'false';
        r.dataset.previousChecked = 'false';
        changed = true;
      });
    });
    if (changed) {
      this.#syncFieldsetState();
      this.updateVariantPickerCss();
    }
    return changed;
  }

  /**
   * "Clear selection" button in each fieldset legend unchecks that fieldset
   * and triggers a re-evaluation.
   */
  #wireClearSelection() {
    this.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const clearBtn = target.closest('.orchard-clear-selection');
      if (!clearBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const fieldset = clearBtn.closest('fieldset');
      if (!fieldset) return;
      fieldset.querySelectorAll('input[type="radio"]').forEach((r) => {
        if (!(r instanceof HTMLInputElement)) return;
        r.checked = false;
        r.removeAttribute('checked');
        r.dataset.currentChecked = 'false';
        r.dataset.previousChecked = 'false';
      });
      this.#syncFieldsetState();
      this.#updateAvailability();
      this.updateVariantPickerCss();
      this.#updateAddToCartState();
      this.#syncUrlToSelection();
    });
  }

  /**
   * Finds the product-form submit button and syncs its disabled state + label.
   */
  #updateAddToCartState() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const atc = /** @type {HTMLButtonElement | null} */ (
      document.querySelector(
        'product-form-component button[type="submit"], .product-form button[type="submit"]'
      )
    );
    if (!atc) return;

    const variant = this.#findVariantForCurrentSelection();
    const matched = variant !== null;
    const purchasable = matched && variant.available;

    atc.disabled = !purchasable;

    // Orchard (L2 desync safety): keep the submitted variant id in lockstep with
    // the resolved selection. On an incomplete/unavailable selection blank it so a
    // stale-but-valid id from a previous complete selection can't be submitted as
    // the wrong SKU. The clearing discipline makes incomplete states common; the
    // ATC is also disabled above, but this is the no-JS-submit safety net.
    const hiddenId = /** @type {HTMLInputElement | null} */ (
      document.querySelector('product-form-component input[name="id"], .product-form input[name="id"]')
    );
    if (hiddenId) hiddenId.value = purchasable && variant ? String(variant.id) : '';

    const textEl = atc.querySelector('.add-to-cart-text, [data-key="add-to-cart-text"]');
    if (!textEl) return;

    if (!matched) {
      // Orchard: locale-aware label from data-text-* (set by add-to-cart-button.liquid).
      // When Colour is the ONLY unselected dimension (every higher one is chosen),
      // prompt for it specifically; otherwise fall back to the generic label.
      const selections = this.#getCurrentSelections();
      const ranks = this.#getRanks();
      const colourIdx = ranks.findIndex((r) => r === RANK_COLOUR);
      const onlyColourMissing =
        colourIdx !== -1 &&
        selections[colourIdx] == null &&
        ranks.every((_rank, i) => i === colourIdx || selections[i] != null);

      textEl.textContent = onlyColourMissing
        ? atc.dataset.textChooseColour || 'Choose Colour To Add To Cart'
        : atc.dataset.textChooseOptions || 'Choose Options To Add To Cart';
      return;
    }

    if (!purchasable) {
      // Orchard: locale-aware label
      textEl.textContent = atc.dataset.textSoldOut || 'Sold Out';
      return;
    }

    // Let Horizon's morph set the price; give it a frame to arrive.
    requestAnimationFrame(() => {
      const priceEl = document.querySelector('product-price .price, .product-price .price');
      const price = priceEl ? (priceEl.textContent || '').trim() : '';
      // Orchard: locale-aware base label
      const baseText = atc.dataset.textAddToCart || 'Add to cart';
      textEl.textContent = price ? `${baseText} ${price}` : baseText;
    });
  }

  /**
   * On initial load, if ?variant= is present but the URL's variant is no
   * longer reflected in the picker (because we deselected stale+disabled
   * radios), strip the param so the URL matches the visible state.
   */
  #syncUrlToSelection() {
    if (this.dataset.templateProductMatch !== 'true') return;
    const url = new URL(window.location.href);
    const variantParam = url.searchParams.get('variant');
    if (!variantParam) return;

    const matched = this.#findVariantForCurrentSelection();
    if (matched && String(matched.id) === variantParam) return;

    url.searchParams.delete('variant');
    history.replaceState({}, '', url.toString());
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.#getFieldsets();
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = this.#getFieldsets();
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // The inline variant catalog may have been swapped during morph
        // (combined-listing child products ship their own catalog). Drop the
        // cache so the next lookup reads from the freshly-morphed DOM.
        this.#invalidateVariantDataCache();
        if (this.isConnected) {
          this.#syncFieldsetState();
          this.#updateAvailability();
          this.#updateAddToCartState();
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#syncFieldsetState();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = this.#getFieldsets();

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   *
   * Orchard: also collects option value IDs from collapsed single-value
   * elements ([data-testid="variant-option-single"]), which have no radio or
   * <select>. Without this, BNIB-style products (where Condition has only one
   * value) send an incomplete option_values= request to the server.
   *
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked, [data-testid="variant-option-single"][data-option-value-id]'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Orchard (L3 submit-guard support): the variant id the current selection
   * resolves to, recomputed fresh from the DOM + local catalog — it never trusts
   * the hidden input or the URL.
   *   - string    → resolves to an available variant (safe to submit)
   *   - null      → has a local catalog but the selection is partial/unavailable
   *   - undefined → no local catalog (product cards / non-phone; server resolves)
   * @returns {string | null | undefined}
   */
  getSubmitVariantId() {
    if (this.#getVariantData().length === 0) return undefined;
    const variant = this.#findVariantForCurrentSelection();
    return variant && variant.available ? String(variant.id) : null;
  }

  /** Orchard (L3): re-sync the Add-to-Cart button label/disabled state externally. */
  syncAddToCartState() {
    this.#updateAddToCartState();
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
